import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import {
  ButtonKind,
  ButtonSize,
  CheckIcon,
  Input,
  InputKind,
  InputType,
  ToastA11Y,
  ToastTiming,
  useToast,
} from "@viagogo/core/ui";
import type { ISiteMessage } from "src/http/types";
import { Content, useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { retryFetch } from "src/shared/utils/http/fetch-sender";
import { exponentialBackoffRetryDelay, THREE_RETRIES } from "src/shared/utils/query/retry-constants";
import { getCurrentParameters, writeUrl } from "src/shared/utils/url-builder";
import { EmailPrivacyDisclosure } from "src/website-apps/shared/components/disclosures";
import { validateEmail } from "src/website-apps/shared/utils/validateEmail";
import { writeEmailOptInSubscribed } from "../email-opt-in-subscription-storage";
import {
  ContentViewContainer,
  ContentViewTextContainer,
  SubscribeButton,
  SubscribeButtonContainer,
} from "./styled-components";

export interface EmailOptInContentViewProps {
  readonly email?: string | undefined;
  readonly privacyPolicyMessage?: ISiteMessage | undefined;
  readonly onEmailSignUp: () => void;
}

export const EmailOptInContentView = (props: EmailOptInContentViewProps) => {
  const { email: initialEmail, privacyPolicyMessage, onEmailSignUp } = props;

  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const emailPlaceHolder = useContent("cid$#3376734#"); // "Email address"
  const [email, setEmail] = useState(initialEmail ?? "");
  const [isEmailValid, setIsEmailValid] = useState(() => !!initialEmail && validateEmail(initialEmail));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { enqueueToast } = useToast();

  // Modal autofocuses the email input on desktop; the mobile bottom sheet does not, so only
  // autofocus above the "tablet" breakpoint where Modal renders as a centered dialog.
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isDesktop) {
      inputRef.current?.focus();
    }
  }, [isDesktop]);

  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setIsEmailValid(!!newEmail && validateEmail(newEmail));
  }, []);

  const onSubmit = useCallback(async () => {
    if (!isEmailValid || isSubmitting) return;
    setIsSubmitting(true);
    const postBody = new URLSearchParams();
    postBody.append("email", email);
    const csrfToken = document.querySelector<HTMLInputElement>("#x-csrf-token")?.value ?? "";
    try {
      const data = await retryFetch<{ success: boolean }>(
        writeUrl("/AjaxService/Signup/1", getCurrentParameters(window.location.search), appContext),
        {
          method: "post",
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            "x-csrf-token": csrfToken,
          },
          body: postBody.toString(),
        },
        { retries: THREE_RETRIES, waitTime: exponentialBackoffRetryDelay },
      );
      if (!data.success) return;
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.EMAIL_SIGNUP, "");
      writeEmailOptInSubscribed();
      enqueueToast({
        icon: CheckIcon,
        content: (
          <p>
            <Content id="cid$#4206447#" /> {/* "You are now subscribed" */}
          </p>
        ),
        showCloseIcon: false,
        a11y: ToastA11Y.passive,
        timing: ToastTiming.short,
      });
      onEmailSignUp();
    } catch {
      // Sign-up failure is silently ignored to match prior behavior
    } finally {
      setIsSubmitting(false);
    }
  }, [appContext, email, isEmailValid, isSubmitting, enqueueToast, onEmailSignUp, storePageEvent]);

  return (
    <ContentViewContainer>
      <ContentViewTextContainer>
        <Input
          ref={inputRef}
          label={<Content id="cid$#1808903#" />}
          hiddenLabel
          placeholder={emailPlaceHolder}
          kind={InputKind.box}
          onChange={onEmailChange}
          value={email}
          type={InputType.email}
          block={false}
          endIcon={email && isEmailValid ? <CheckIcon /> : undefined}
        />
        {privacyPolicyMessage !== undefined && privacyPolicyMessage.hasValue && (
          <EmailPrivacyDisclosure
            emailPrivacyDisclosureSiteMessage={{
              message: privacyPolicyMessage.message,
              disclaimer: privacyPolicyMessage.disclaimer,
              qualifier: privacyPolicyMessage.qualifier,
              featureTrackingKey: privacyPolicyMessage.featureTrackingKey,
            }}
            paddingTop={0}
          />
        )}
      </ContentViewTextContainer>
      <SubscribeButtonContainer>
        <SubscribeButton
          type="submit"
          kind={ButtonKind.primary}
          size={ButtonSize.default}
          onClick={onSubmit}
          disabled={isSubmitting || email.length === 0 || !isEmailValid}
        >
          <Content id="cid$#3869383#" /> {/* "Subscribe" */}
        </SubscribeButton>
      </SubscribeButtonContainer>
    </ContentViewContainer>
  );
};
